/**
 * WCAG 2.1 relative-luminance contrast ratio (https://www.w3.org/TR/WCAG21/#dfn-relative-luminance).
 *
 * Shared by the runtime custom-accent legibility warning (Settings.tsx: "this accent may be hard
 * to read on buttons") and the palette contrast tests (tokens.contrast.test.ts, theme.test.ts) --
 * one implementation so the number a user sees warned about and the number a test asserts on can
 * never drift apart.
 */

function channel(value: number): number {
  const c = value / 255;
  return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

/** Relative luminance of a `#rrggbb` hex colour, 0 (black) to 1 (white). */
export function relativeLuminance(hex: string): number {
  const n = hex.replace('#', '');
  const r = parseInt(n.slice(0, 2), 16);
  const g = parseInt(n.slice(2, 4), 16);
  const b = parseInt(n.slice(4, 6), 16);
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

/** WCAG contrast ratio between two `#rrggbb` hex colours, from 1:1 (identical) to 21:1. */
export function contrastRatio(hexA: string, hexB: string): number {
  const sorted = [relativeLuminance(hexA), relativeLuminance(hexB)].sort((x, y) => y - x);
  const hi = sorted[0] ?? 0;
  const lo = sorted[1] ?? 0;
  return (hi + 0.05) / (lo + 0.05);
}
